/**
 * Minimal, dependency-free .xlsx writer.
 *
 * A real OOXML workbook (ZIP of XML parts), so Excel opens it without the
 * "format and extension don't match" warning that SpreadsheetML-named-.xls
 * triggers. No SheetJS: the npm `xlsx` package is stale/CVE-prone, and the
 * only piece we actually need from a ZIP library is stored (uncompressed)
 * entries + CRC-32, which is ~60 lines.
 *
 * Sheet layout: every record occupies TWO rows — "was" on top, "new" below.
 * Columns holding one value per record (SN, slot, type, …) are merged
 * vertically across the pair; changed "new" cells are highlighted.
 */

import { diffChars, Seg } from './diff';

export type XlsCell = string | number | null | undefined;

export interface XlsxColumn {
  header: string;
  /** Approximate width in characters. */
  width?: number;
}

export interface XlsxRecord {
  /** One value per `single` column — vertically merged over the two rows. */
  single: XlsCell[];
  /** Value per `pair` column before the change. */
  was: XlsCell[];
  /** Value per `pair` column after it (the resulting value, not just the diff). */
  now: XlsCell[];
}

export interface XlsxSheet {
  name: string;
  single: XlsxColumn[];
  pair: XlsxColumn[];
  /** Row markers for the stacked pair, e.g. "was" / "new". */
  wasLabel: string;
  nowLabel: string;
}

/* ------------------------------------------------------------------ XML --- */

const esc = (v: string): string =>
  v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // XML 1.0 forbids these outright — Excel refuses the whole file over one.
    .replace(/[\x00-\x08\x0b\x0c\x0e-\x1f]/g, '');

/** 0-based column index → "A", "B", … "AA". */
function colRef(i: number): string {
  let s = '';
  let n = i + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Style ids, matching the cellXfs order in STYLES below. */
const S_HEADER = 1;
const S_CELL = 2;
const S_CHANGED = 3;
const S_MERGED = 4;
const S_LABEL = 5;

function cellXml(ref: string, v: XlsCell, style: number): string {
  if (v === null || v === undefined || v === '') return `<c r="${ref}" s="${style}"/>`;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return `<c r="${ref}" s="${style}"><v>${v}</v></c>`;
  }
  return `<c r="${ref}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${esc(String(v))}</t></is></c>`;
}

/** Inline-string cell whose differing characters are red. `bold` keeps the
 * emphasis the "new" row already had before rich runs took over its font. */
function diffCellXml(ref: string, segs: Seg[], style: number, bold: boolean): string {
  if (segs.length === 0) return `<c r="${ref}" s="${style}"/>`;
  const runs = segs
    .map((s) => {
      // Run order matches what Excel itself emits (b, sz, color, rFont).
      const rPr =
        '<rPr>' +
        (bold ? '<b/>' : '') +
        '<sz val="11"/>' +
        (s.diff ? '<color rgb="FFFF0000"/>' : '') +
        '<rFont val="Calibri"/>' +
        '</rPr>';
      return `<r>${rPr}<t xml:space="preserve">${esc(s.text)}</t></r>`;
    })
    .join('');
  return `<c r="${ref}" s="${style}" t="inlineStr"><is>${runs}</is></c>`;
}

const STYLES =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<fonts count="3">' +
  '<font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/></font>' +
  '<font><i/><sz val="9"/><color rgb="FF888888"/><name val="Calibri"/></font>' +
  '</fonts>' +
  '<fills count="4">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FFEDEDED"/><bgColor indexed="64"/></patternFill></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FFFFF2CC"/><bgColor indexed="64"/></patternFill></fill>' +
  '</fills>' +
  '<borders count="2">' +
  '<border><left/><right/><top/><bottom/><diagonal/></border>' +
  '<border>' +
  '<left style="thin"><color rgb="FFD9D9D9"/></left><right style="thin"><color rgb="FFD9D9D9"/></right>' +
  '<top style="thin"><color rgb="FFD9D9D9"/></top><bottom style="thin"><color rgb="FFD9D9D9"/></bottom>' +
  '<diagonal/></border>' +
  '</borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="6">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>' +
  '<xf numFmtId="0" fontId="1" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>' +
  '<xf numFmtId="0" fontId="2" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="top"/></xf>' +
  '</cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '</styleSheet>';

/** Build the worksheet part: header row, then two rows per record. */
function buildSheet(sheet: XlsxSheet, records: XlsxRecord[]): string {
  // Column order: [single…][was/new marker][pair…]
  const nSingle = sheet.single.length;
  const markerAt = nSingle;
  const pairAt = nSingle + 1;
  const total = pairAt + sheet.pair.length;

  const cols = [
    ...sheet.single.map((c) => c.width ?? 16),
    7, // the was/new marker
    ...sheet.pair.map((c) => c.width ?? 18),
  ]
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join('');

  const rows: string[] = [];
  const merges: string[] = [];

  const headers = [...sheet.single.map((c) => c.header), '', ...sheet.pair.map((c) => c.header)];
  rows.push(
    `<row r="1" ht="30" customHeight="1">` +
      headers.map((h, i) => cellXml(`${colRef(i)}1`, h, S_HEADER)).join('') +
      '</row>',
  );

  const same = (a: XlsCell, b: XlsCell): boolean =>
    (a === null || a === undefined ? '' : String(a)) === (b === null || b === undefined ? '' : String(b));

  records.forEach((rec, idx) => {
    const top = 2 + idx * 2;
    const bottom = top + 1;
    const topCells: string[] = [];
    const botCells: string[] = [];

    for (let i = 0; i < nSingle; i++) {
      topCells.push(cellXml(`${colRef(i)}${top}`, rec.single[i], S_MERGED));
      // The merged range still needs the covered cell to exist, styled, so the
      // border renders around the whole block.
      botCells.push(cellXml(`${colRef(i)}${bottom}`, null, S_MERGED));
      merges.push(`<mergeCell ref="${colRef(i)}${top}:${colRef(i)}${bottom}"/>`);
    }
    topCells.push(cellXml(`${colRef(markerAt)}${top}`, sheet.wasLabel, S_LABEL));
    botCells.push(cellXml(`${colRef(markerAt)}${bottom}`, sheet.nowLabel, S_LABEL));

    for (let i = 0; i < sheet.pair.length; i++) {
      const col = colRef(pairAt + i);
      const w = rec.was[i];
      const n = rec.now[i];
      // When both sides are non-empty strings that differ, paint the changed
      // characters red on each side; otherwise fall back to a plain cell.
      if (typeof w === 'string' && typeof n === 'string' && w !== '' && n !== '' && w !== n) {
        const d = diffChars(w, n);
        topCells.push(diffCellXml(`${col}${top}`, d.was, S_CELL, false));
        botCells.push(diffCellXml(`${col}${bottom}`, d.now, S_CHANGED, true));
      } else {
        topCells.push(cellXml(`${col}${top}`, w, S_CELL));
        botCells.push(cellXml(`${col}${bottom}`, n, same(w, n) ? S_CELL : S_CHANGED));
      }
    }

    rows.push(`<row r="${top}">${topCells.join('')}</row>`);
    rows.push(`<row r="${bottom}">${botCells.join('')}</row>`);
  });

  const lastRow = Math.max(1, 1 + records.length * 2);
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    `<dimension ref="A1:${colRef(total - 1)}${lastRow}"/>` +
    '<sheetViews><sheetView workbookViewId="0">' +
    '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>' +
    '</sheetView></sheetViews>' +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    `<cols>${cols}</cols>` +
    `<sheetData>${rows.join('')}</sheetData>` +
    (merges.length ? `<mergeCells count="${merges.length}">${merges.join('')}</mergeCells>` : '') +
    '</worksheet>'
  );
}

/* ------------------------------------------------------------------ ZIP --- */

let crcTable: Uint32Array | null = null;
function crc32(data: Uint8Array): number {
  if (!crcTable) {
    const tbl = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      tbl[n] = c >>> 0;
    }
    crcTable = tbl;
  }
  const tbl = crcTable;
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = tbl[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** ZIP archive with STORED (uncompressed) entries — the parts are small XML
 * blobs and skipping deflate keeps this dependency-free. */
function zip(files: Array<{ name: string; data: Uint8Array }>): Blob {
  const enc = new TextEncoder();
  const now = new Date();
  const dosTime =
    (now.getHours() << 11) | (now.getMinutes() << 5) | Math.floor(now.getSeconds() / 2);
  const dosDate =
    ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();

  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const f of files) {
    const name = enc.encode(f.name);
    const crc = crc32(f.data);

    const lh = new Uint8Array(30 + name.length);
    const lv = new DataView(lh.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0x0800, true); // UTF-8 names
    lv.setUint16(8, 0, true); // stored
    lv.setUint16(10, dosTime, true);
    lv.setUint16(12, dosDate, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, f.data.length, true);
    lv.setUint32(22, f.data.length, true);
    lv.setUint16(26, name.length, true);
    lh.set(name, 30);
    locals.push(lh, f.data);

    const ch = new Uint8Array(46 + name.length);
    const cv = new DataView(ch.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, dosTime, true);
    cv.setUint16(14, dosDate, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, f.data.length, true);
    cv.setUint32(24, f.data.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    ch.set(name, 46);
    centrals.push(ch);

    offset += lh.length + f.data.length;
  }

  const cdSize = centrals.reduce((n, c) => n + c.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true);

  return new Blob([...locals, ...centrals, eocd] as BlobPart[], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/* --------------------------------------------------------------- public --- */

const REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const DOC_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

/** Build the whole workbook as a downloadable Blob. */
export function buildXlsx(sheet: XlsxSheet, records: XlsxRecord[]): Blob {
  const enc = new TextEncoder();
  // Excel caps sheet names at 31 chars and forbids : \ / ? * [ ]
  const name = esc(sheet.name.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31)) || 'Sheet1';

  const parts: Array<{ name: string; xml: string }> = [
    {
      name: '[Content_Types].xml',
      xml:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        '</Types>',
    },
    {
      name: '_rels/.rels',
      xml:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        `<Relationships xmlns="${REL_NS}">` +
        `<Relationship Id="rId1" Type="${DOC_NS}/officeDocument" Target="xl/workbook.xml"/>` +
        '</Relationships>',
    },
    {
      name: 'xl/workbook.xml',
      xml:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
        ` xmlns:r="${DOC_NS}">` +
        `<sheets><sheet name="${name}" sheetId="1" r:id="rId1"/></sheets>` +
        '</workbook>',
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      xml:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        `<Relationships xmlns="${REL_NS}">` +
        `<Relationship Id="rId1" Type="${DOC_NS}/worksheet" Target="worksheets/sheet1.xml"/>` +
        `<Relationship Id="rId2" Type="${DOC_NS}/styles" Target="styles.xml"/>` +
        '</Relationships>',
    },
    { name: 'xl/styles.xml', xml: STYLES },
    { name: 'xl/worksheets/sheet1.xml', xml: buildSheet(sheet, records) },
  ];

  return zip(parts.map((p) => ({ name: p.name, data: enc.encode(p.xml) })));
}

/** Build the workbook and hand it to the browser as a download. */
export function downloadXlsx(filename: string, sheet: XlsxSheet, records: XlsxRecord[]): void {
  const url = URL.createObjectURL(buildXlsx(sheet, records));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** `YYYY-MM-DD_HH-MM` stamp for export filenames. */
export function stampNow(): string {
  const d = new Date();
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}-${p(d.getMinutes())}`;
}
